import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

export const LegalLayout = ({ seoTitle, seoDesc, path, title, updated, children }) => (
  <div className="min-h-screen bg-white">
    <SEO title={seoTitle} description={seoDesc} path={path} />
    <section className="bg-[#003057] pt-40 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl md:text-5xl font-bold text-white">{title}</h1>
        {updated && <p className="text-gray-300 mt-3">Last updated: {updated}</p>}
      </div>
    </section>
    <section className="py-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 legal-content"
      >
        {children}
      </motion.div>
    </section>
  </div>
);

const H = ({ children }) => <h2 className="text-2xl font-bold text-[#003057] mt-8 mb-3">{children}</h2>;
const P = ({ children }) => <p className="text-[#4A4A4A] leading-relaxed mb-4">{children}</p>;

export const PrivacyPolicy = () => (
  <LegalLayout
    seoTitle="Privacy Policy | Aero Time Critical"
    seoDesc="How Aero Time Critical Ltd collects, uses and protects your personal data."
    path="/privacy-policy"
    title="Privacy Policy"
    updated="December 2025"
  >
    <P>Aero Time Critical Ltd ("we", "us", "our") is committed to protecting your privacy. This policy explains how we collect, use, store and protect personal data submitted through this website, in accordance with UK GDPR and the Data Protection Act 2018.</P>
    <H>Information We Collect</H>
    <P>We collect information you provide via our quote and contact forms, including your name, company, email address, telephone number, shipment details and any documents you upload. We also record submission metadata such as date, time and enquiry reference.</P>
    <H>How We Use Your Information</H>
    <P>We use your information to respond to enquiries, provide quotations, arrange and manage shipments, and communicate with you about your requirements. Our lawful bases are your consent and our legitimate interest in operating our freight services.</P>
    <H>Data Storage & Security</H>
    <P>Submissions and uploaded documents are stored securely with restricted access. Uploaded files are stored privately and are not publicly accessible. We apply appropriate technical and organisational measures to protect your data.</P>
    <H>Data Retention</H>
    <P>We retain enquiry data only for as long as necessary to fulfil the purposes described, comply with legal obligations, and manage our commercial relationship.</P>
    <H>Your Rights</H>
    <P>You have the right to access, correct, or request deletion of your personal data, and to withdraw consent at any time. To exercise these rights, contact booking@aerotimecritical.co.uk.</P>
    <H>Contact</H>
    <P>Aero Time Critical Ltd, 268 Bath Road, Slough, Berkshire, SL1 4DX, United Kingdom. Email: booking@aerotimecritical.co.uk. Telephone: +44 (0) 1753 720055.</P>
  </LegalLayout>
);

export const CookiePolicy = () => (
  <LegalLayout
    seoTitle="Cookie Policy | Aero Time Critical"
    seoDesc="How Aero Time Critical Ltd uses cookies and how you can control them."
    path="/cookie-policy"
    title="Cookie Policy"
    updated="December 2025"
  >
    <P>This Cookie Policy explains how Aero Time Critical Ltd uses cookies and similar technologies on this website.</P>
    <H>What Are Cookies</H>
    <P>Cookies are small text files stored on your device that help websites function and provide information to site owners.</P>
    <H>Cookies We Use</H>
    <P>We use essential cookies required for the website to function. With your consent, we may use non-essential analytics cookies to understand how visitors use our site so we can improve it.</P>
    <H>Managing Cookies</H>
    <P>You can accept or reject non-essential cookies using our cookie banner, and you can change your browser settings to block or delete cookies at any time. Blocking essential cookies may affect site functionality.</P>
    <H>Contact</H>
    <P>For questions about our use of cookies, contact booking@aerotimecritical.co.uk.</P>
  </LegalLayout>
);

export const Terms = () => (
  <LegalLayout
    seoTitle="Terms & Conditions | Aero Time Critical"
    seoDesc="Website terms of use and terms and conditions for Aero Time Critical Ltd."
    path="/terms"
    title="Terms & Conditions"
    updated="December 2025"
  >
    <H>Website Terms of Use</H>
    <P>By accessing this website you agree to use it lawfully and not in any way that may damage, disable or impair the site. Content on this website is provided for general information about our services and does not constitute a binding offer.</P>
    <H>Terms & Conditions of Service</H>
    <P>All freight services are provided subject to our standard trading terms and conditions, which are available on request. Quotations are indicative until confirmed in writing. Service delivery timescales are estimates and depend on carrier availability, customs and factors outside our control.</P>
    <H>Intellectual Property</H>
    <P>All trademarks, logos and content on this website are the property of Aero Time Critical Ltd unless otherwise stated and may not be reproduced without permission.</P>
    <H>Limitation of Liability</H>
    <P>To the fullest extent permitted by law, our liability is limited in accordance with our standard trading conditions. Nothing in these terms excludes liability that cannot be excluded under applicable law.</P>
    <H>Complaints</H>
    <P>We are committed to high standards of service. If you wish to raise a complaint, please contact booking@aerotimecritical.co.uk and we will respond promptly.</P>
    <H>Company Details</H>
    <P>Aero Time Critical Ltd. Registered office: 268 Bath Road, Slough, Berkshire, SL1 4DX, United Kingdom.</P>
  </LegalLayout>
);
